"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateFieldLogic = void 0;
const date_fns_tz_1 = require("date-fns-tz");
const date_fns_1 = require("date-fns");
const intl_1 = require("../../locale/intl");
const texts_1 = require("../../texts");
const date_1 = require("../../aggregations/date");
const field_logic_1 = require("../../field-type-registry/field-logic");
const types_1 = require("@smartsuite/types");
const clipboard_1 = require("../../utils/clipboard");
const clipboard_2 = require("../../clipboard/clipboard");
const comparisons_1 = require("../../comparisons/comparisons");
const date_fieldtype_1 = require("../../safe-field-value/date-fields/date-field/date.fieldtype");
const validation_1 = require("../../validation/validation");
const date_2 = require("../../formatters/date");
const google_sheet_1 = require("../../utils/google-sheet");
const date_3 = require("../../utils/date");
const moment_timezone_1 = require("moment-timezone");
const migration_1 = require("../../utils/migration");
const date_4 = require("../../filter/date");
const dateDisplayFormats = ["compact", "standard", "long"];
class DateFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.aggregates = {
            empty: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.empty),
                aggregator: field_logic_1.countEmpty,
            },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: field_logic_1.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: field_logic_1.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: field_logic_1.percentEmpty,
            },
            "earliest-date": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.earliest_date),
                aggregator: (0, date_1.dateAggregate)(date_1.minDate),
            },
            "latest-date": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.latest_date),
                aggregator: (0, date_1.dateAggregate)(date_1.maxDate),
            },
            "date-range": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.date_range),
                aggregator: (0, date_1.dateAggregate)((dates, ...props) => {
                    if (!dates.length) {
                        return null;
                    }
                    return `${(0, date_1.minDate)(dates, ...props)} - ${(0, date_1.maxDate)(dates, ...props)}`;
                }),
            },
        };
        this.isDate = true;
        this.name = "datefield";
        this.label = (0, intl_1.$t)(texts_1.dateText.date);
        this.searchSynonyms = [`dd`, `mm`, `yy`];
        this.format = "YYYY-MM-DD";
        this.lookupMode = "lookup";
        this.clipboardPasteRules = {
            datefield: clipboard_1.clipboardToSelf,
            duedatefield: clipboard_2.clipboardDueDateToDate,
            daterangefield: clipboard_2.clipboardDateRangeToDate,
            firstcreatedfield: clipboard_2.clipboardCreatedUpdatedToDate,
            lastupdatedfield: clipboard_2.clipboardCreatedUpdatedToDate,
            textfield: clipboard_2.clipboardTextToDate,
            recordtitlefield: clipboard_2.clipboardTextToDate,
            textareafield: clipboard_2.clipboardTextToDate,
            unknownfield: clipboard_2.clipboardTextToDate,
        };
        this.icon = "date-select";
        this.iconColor = "#6A849B";
        this.placeholder = (0, intl_1.$t)(texts_1.dateFieldText.enter_date_here);
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: comparisons_1.isDate,
            is_not: comparisons_1.isNotDate,
            is_empty: comparisons_1.isEmpty,
            is_not_empty: comparisons_1.isNotEmpty,
            is_before: comparisons_1.isBefore,
            is_after: comparisons_1.isAfter,
            is_on_or_before: comparisons_1.isOnOrBefore,
            is_on_or_after: comparisons_1.isOnOrAfter,
        };
        this.fieldValueGetter = new date_fieldtype_1.DateFieldValueGetter();
    }
    get validators() {
        return { requiredDate: validation_1.requiredDate };
    }
    getFilterComponentData(field, comparison) {
        return {
            ...super.getFilterComponentData(field, comparison),
            dateModes: this.dateModes,
            placeholder: `Type Number of Days`,
        };
    }
    valueToString(fieldValue, field, userPreferences) {
        if (typeof (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date) !== "string")
            return "";
        if (typeof (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.include_time) !== "boolean")
            return "";
        const strValue = userPreferences
            ? (0, date_3.formatDateValueToDisplayFormat)(fieldValue, userPreferences, field.params.display_format, field.params.timezone, field.params.display_timezone, field.params.date_format, field.params.time_format)
            : fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date;
        return strValue !== null && strValue !== void 0 ? strValue : "";
    }
    valueToExport(fieldValue, field, userPreferences) {
        const dateFieldValueFormatter = new date_2.DateFieldValueFormatter();
        const strValue = userPreferences
            ? dateFieldValueFormatter.transform(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date, userPreferences, field.params.display_format, fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.include_time, undefined, true)
            : fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date;
        return strValue !== null && strValue !== void 0 ? strValue : "";
    }
    valueToGoogleSheet(fieldValue, field, userPreferences) {
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date)) {
            return super.valueToGoogleSheet(fieldValue, field, userPreferences);
        }
        let formattedDate = new date_2.DateFieldValueFormatter().prepareFormat(fieldValue.date, field.params.display_format, userPreferences, undefined, true);
        if (fieldValue.include_time) {
            const formattedTime = userPreferences.locale.time_format === "24h" ? "hh:mm" : "hh:mm am/pm";
            formattedDate = (0, intl_1.$t)(texts_1.dateText.date_at_time_export, {
                formattedDate,
                formattedTime,
            });
        }
        const pattern = formattedDate.toLowerCase();
        const zonedDateTime = (0, date_fns_tz_1.utcToZonedTime)(new Date(fieldValue.date), userPreferences.timezone.value);
        const dateString = (0, date_fns_tz_1.format)(zonedDateTime, "yyyy-MM-dd");
        const timeString = (0, date_fns_tz_1.format)(zonedDateTime, "HH:mm:ss");
        const value = fieldValue.include_time
            ? `=DATEVALUE("${dateString}") + TIMEVALUE("${timeString}")`
            : `=DATEVALUE("${dateString}")`;
        return (0, google_sheet_1.makeRowDataValues)(value, "formulaValue", "DATE", pattern);
    }
    getEmptyValue() {
        return (0, date_3.emptyDateValue)();
    }
    getInitialFieldValue(_startValue) {
        return this.getEmptyValue();
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            switchToInputControl: "OnCellClick",
            editorWidth: "100%",
            closeWhenClickOutside: false,
        };
    }
    processFieldValue(record, slug, comparison, field, userPreferences) {
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        return (0, date_3.dateValueToDate)(fieldValue, userPreferences.timezone.value);
    }
    processFilterValue(filterValue, comparison, _field, userPreferences) {
        const emptyComparisons = ["is_empty", "is_not_empty"];
        if (emptyComparisons.includes(comparison)) {
            return filterValue;
        }
        const helper = new date_4.BaseDateModeHelper(comparison, filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode, filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode_value, userPreferences);
        // @ts-expect-error: Need to resolve
        const valueGetter = helper[filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode];
        if (typeof valueGetter !== "function") {
            return filterValue;
        }
        return valueGetter();
    }
    filterToRecordValue(filterValue, comparison, field, userPreferences) {
        var _a;
        if (comparison !== "is") {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const now = (0, date_3.getNow)(userPreferences.timezone.value);
        const today = (0, date_fns_1.startOfDay)(now);
        if (filterValue.date_mode === "today") {
            return (0, date_3.dateToDateValue)(today);
        }
        if (filterValue.date_mode === "tomorrow") {
            return (0, date_3.dateToDateValue)((0, date_fns_1.addDays)(today, 1));
        }
        if (filterValue.date_mode === "yesterday") {
            return (0, date_3.dateToDateValue)((0, date_fns_1.addDays)(today, -1));
        }
        if ((0, date_4.isDateFilterRangeValue)(filterValue) &&
            ((_a = filterValue.date_mode_value) === null || _a === void 0 ? void 0 : _a.lower)) {
            const isExactDate = filterValue.date_mode_value.lower === filterValue.date_mode_value.upper;
            if (isExactDate) {
                const date = (0, date_fns_1.startOfDay)(new Date(filterValue.date_mode_value.lower));
                return (0, date_3.dateToDateValue)(date);
            }
        }
        throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
    }
    getDateValueFromString(date, includeTime) {
        return { date, include_time: includeTime };
    }
    addDuration(fieldValue, duration, userPreferences, includeTime) {
        return (0, date_3.addDurationToDateValue)(fieldValue, duration, userPreferences, includeTime);
    }
    isValueEmpty(fieldValue) {
        return !(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date);
    }
    isValueEqual(fieldValue1, fieldValue2, _field, userPreferences) {
        var _a;
        return (0, date_3.isDateValuesEqual)(fieldValue1, fieldValue2, (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone.value) !== null && _a !== void 0 ? _a : "UTC");
    }
    getDefaultValue(field, userPreferences) {
        var _a;
        // Default value for date field is always string
        const defaultValue = (0, date_3.parseDefaultValue)(super.getDefaultValue(field, userPreferences), (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone.value) !== null && _a !== void 0 ? _a : "UTC");
        if (!defaultValue) {
            return { date: null, include_time: false };
        }
        return { date: defaultValue, include_time: false };
    }
    prepareValueForClipboard(fieldValue, field, userPreferences) {
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date)) {
            return { text: "", rawValue: fieldValue };
        }
        return {
            text: (0, date_3.formatDateValueToDisplayFormat)(fieldValue, userPreferences, field.params.display_format, field.params.timezone, field.params.display_timezone, field.params.date_format, field.params.time_format, true, " "),
            rawValue: fieldValue,
        };
    }
    getGridColumnSettings(field) {
        return {
            ...super.getGridColumnSettings(field),
            contentAlignment: "centered",
        };
    }
    searchKey(fieldValue, field, userPreferences) {
        const stringValue = this.valueToString(fieldValue, field, userPreferences);
        if (!stringValue) {
            return "";
        }
        const date = (0, date_3.dateToMoment)(fieldValue, userPreferences);
        const year = date && (0, moment_timezone_1.isMoment)(date) ? date.year().toString() : null;
        let searchKey = stringValue;
        if (year && !searchKey.includes(year)) {
            searchKey = `${searchKey} ${year}`;
        }
        return searchKey;
    }
    sortKey(fieldValue, field, userPreferences) {
        var _a;
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date)) {
            return "";
        }
        if (fieldValue.include_time) {
            const timezone = (_a = field.params.timezone) !== null && _a !== void 0 ? _a : userPreferences.timezone.value;
            const date = (0, date_fns_tz_1.utcToZonedTime)(fieldValue.date, timezone);
            return (0, date_3.formatIsoWithTzOffset)(date);
        }
        return fieldValue.date.substring(0, 10);
    }
    useNaturalSorting(field, fieldValue, isGroupByRule) {
        return false;
    }
    groupByKey(fieldValue, field, userPreferences) {
        // Use a date without a time to define the start of a period.
        return this.sortKey(fieldValue, field, userPreferences).substring(0, 10);
    }
    groupBySortKey(value, field, userPreferences) {
        return this.groupByKey(value, field, userPreferences);
    }
    replaceValue(field, record, sourceValue, targetValue, userPreferences) {
        if (this.isValueEmpty(targetValue)) {
            return this.getEmptyValue();
        }
        const date = (0, date_3.dateValueToDate)(targetValue, userPreferences.timezone.value);
        if (!date) {
            return this.getEmptyValue();
        }
        return (0, date_3.replaceDate)(sourceValue, userPreferences.timezone.value, date.getFullYear(), date.getMonth(), date.getDate());
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 120,
            minWidth: 60,
        };
    }
    getRecordTitleVariableValue(variableName, fieldValue, field, userPreferences) {
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date)) {
            return "";
        }
        return (0, date_3.formatDateValueToDisplayFormat)(fieldValue, userPreferences, field.params.display_format, field.params.timezone, field.params.display_timezone, field.params.date_format, field.params.time_format, true);
    }
    migrateField(fieldTo, fieldFrom) {
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, dateDisplayFormats, fieldTo.params.display_format);
    }
    getEmptyFilterValue(_field, comparison) {
        var _a;
        const comparisonsWithNullValue = ["is_empty", "is_not_empty"].includes(comparison);
        if (comparisonsWithNullValue) {
            return null;
        }
        const dateMode = (_a = this.dateModes[comparison]) !== null && _a !== void 0 ? _a : this.dateModes.default;
        if (!dateMode) {
            return null;
        }
        const defaultDateComparison = dateMode[0];
        return (0, date_4.getDateEmptyFilterValue)(defaultDateComparison.value, defaultDateComparison.label);
    }
}
exports.DateFieldLogic = DateFieldLogic;
